import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

interface AnalyticsData {
  overview: {
    totalRevenue: number;
    totalPatrons: number;
    averagePledge: number;
    growthRate: number;
  };
  trends: {
    date: string;
    revenue: number;
    patrons: number;
    newPatrons: number;
    churnedPatrons: number;
  }[];
  topTiers: {
    id: string;
    name: string;
    patronCount: number;
    revenue: number;
  }[];
  demographics: {
    country: string;
    patronCount: number;
    percentage: number;
  }[];
}

const CreatorAdvancedAnalyticsPage: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [selectedMetric, setSelectedMetric] = useState<'revenue' | 'patrons' | 'growth'>('revenue');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      // Simulated data for now - in real app would call analyticsService
      const mockData: AnalyticsData = {
        overview: {
          totalRevenue: 45678.90,
          totalPatrons: 1234,
          averagePledge: 37.01,
          growthRate: 12.5,
        },
        trends: generateMockTrends(timeRange),
        topTiers: [
          { id: '1', name: 'Platinum', patronCount: 45, revenue: 13500 },
          { id: '2', name: 'Gold', patronCount: 123, revenue: 12300 },
          { id: '3', name: 'Silver', patronCount: 456, revenue: 9120 },
          { id: '4', name: 'Bronze', patronCount: 610, revenue: 10980 },
        ],
        demographics: [
          { country: 'United States', patronCount: 567, percentage: 45.9 },
          { country: 'United Kingdom', patronCount: 234, percentage: 19.0 },
          { country: 'Canada', patronCount: 156, percentage: 12.6 },
          { country: 'Australia', patronCount: 89, percentage: 7.2 },
          { country: 'Other', patronCount: 188, percentage: 15.3 },
        ],
      };
      
      setAnalytics(mockData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMockTrends = (range: string) => {
    const days = range === '7d' ? 7 : range === '30d' ? 30 : range === '90d' ? 90 : 365;
    const trends = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      trends.push({
        date: date.toISOString().split('T')[0],
        revenue: Math.random() * 2000 + 1000,
        patrons: Math.floor(Math.random() * 100 + 1000),
        newPatrons: Math.floor(Math.random() * 20 + 5),
        churnedPatrons: Math.floor(Math.random() * 10 + 2),
      });
    }
    
    return trends;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        <span className="ml-3 text-gray-600">Loading analytics...</span>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">No Analytics Data</h3>
          <p className="mt-1 text-sm text-gray-500">
            Unable to load analytics data at this time.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Advanced Analytics</h1>
          <p className="mt-2 text-sm text-gray-600">
            Deep insights into your creator performance and patron behavior.
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-5 w-5 text-gray-400" />
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Total Revenue</p>
              <p className="text-3xl font-bold text-green-900 mt-2">
                ${analytics.overview.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
              <div className="flex items-center mt-2">
                <ArrowTrendingUpIcon className="h-4 w-4 text-green-600 mr-1" />
                <span className="text-sm text-green-600 font-medium">
                  +{analytics.overview.growthRate}%
                </span>
              </div>
            </div>
            <CurrencyDollarIcon className="h-12 w-12 text-green-500" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Patrons</p>
              <p className="text-3xl font-bold text-blue-900 mt-2">
                {analytics.overview.totalPatrons.toLocaleString()}
              </p>
              <div className="flex items-center mt-2">
                <span className="text-sm text-blue-600">
                  Active supporters
                </span>
              </div>
            </div>
            <UserGroupIcon className="h-12 w-12 text-blue-500" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Average Pledge</p>
              <p className="text-3xl font-bold text-purple-900 mt-2">
                ${analytics.overview.averagePledge.toFixed(2)}
              </p>
              <div className="flex items-center mt-2">
                <span className="text-sm text-purple-600">
                  Per patron
                </span>
              </div>
            </div>
            <ChartBarIcon className="h-12 w-12 text-purple-500" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">Growth Rate</p>
              <p className="text-3xl font-bold text-yellow-900 mt-2">
                {analytics.overview.growthRate}%
              </p>
              <div className="flex items-center mt-2">
                <ArrowTrendingUpIcon className="h-4 w-4 text-yellow-600 mr-1" />
                <span className="text-sm text-yellow-600">
                  Month over month
                </span>
              </div>
            </div>
            <ArrowTrendingUpIcon className="h-12 w-12 text-yellow-500" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Main Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Trends Over Time</h2>
            <div className="flex space-x-2">
              <button
                onClick={() => setSelectedMetric('revenue')}
                className={`px-3 py-1 text-sm rounded-md ${
                  selectedMetric === 'revenue'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Revenue
              </button>
              <button
                onClick={() => setSelectedMetric('patrons')}
                className={`px-3 py-1 text-sm rounded-md ${
                  selectedMetric === 'patrons'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Patrons
              </button>
              <button
                onClick={() => setSelectedMetric('growth')}
                className={`px-3 py-1 text-sm rounded-md ${
                  selectedMetric === 'growth'
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Growth
              </button>
            </div>
          </div>

          {/* Simple bar chart visualization */}
          <div className="space-y-2">
            {analytics.trends.slice(-10).map((trend, index) => {
              const value = selectedMetric === 'revenue' 
                ? trend.revenue 
                : selectedMetric === 'patrons'
                ? trend.patrons
                : trend.newPatrons - trend.churnedPatrons;
              
              const maxValue = selectedMetric === 'revenue' ? 3000 : selectedMetric === 'patrons' ? 1200 : 30;
              const percentage = (value / maxValue) * 100;
              
              return (
                <div key={index} className="flex items-center">
                  <div className="w-24 text-xs text-gray-600">{new Date(trend.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                  <div className="flex-1 bg-gray-200 rounded-full h-6 overflow-hidden">
                    <div
                      className={`h-full flex items-center justify-end pr-2 text-xs font-medium text-white transition-all duration-500 ${
                        selectedMetric === 'revenue'
                          ? 'bg-gradient-to-r from-green-400 to-green-600'
                          : selectedMetric === 'patrons'
                          ? 'bg-gradient-to-r from-blue-400 to-blue-600'
                          : value >= 0
                          ? 'bg-gradient-to-r from-purple-400 to-purple-600'
                          : 'bg-gradient-to-r from-red-400 to-red-600'
                      }`}
                      style={{ width: `${Math.max(percentage, 5)}%` }}
                    >
                      {selectedMetric === 'revenue' ? `$${value.toFixed(0)}` : Math.round(value)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Tiers */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Top Performing Tiers</h2>
          <div className="space-y-4">
            {analytics.topTiers.map((tier, index) => (
              <div key={tier.id} className="flex items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-bold text-indigo-700">#{index + 1}</span>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-900">{tier.name}</p>
                  <p className="text-xs text-gray-500">{tier.patronCount} patrons</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">${tier.revenue.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">
                    ${(tier.revenue / tier.patronCount).toFixed(2)}/patron
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Demographics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Patron Demographics</h2>
        <div className="space-y-4">
          {analytics.demographics.map((demo) => (
            <div key={demo.country}>
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{demo.country}</span>
                <span className="text-sm text-gray-500">
                  {demo.patronCount} ({demo.percentage.toFixed(1)}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-indigo-400 to-indigo-600 h-full transition-all duration-500"
                  style={{ width: `${demo.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CreatorAdvancedAnalyticsPage;
